/**
 * MCP (Model Context Protocol) client for TES API
 */

import { getEnvironment } from './oauth';

// Fallback UUID generator for non-secure contexts (HTTP)
function generateUUID(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Fallback for non-secure contexts
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export interface MCPTool {
  name: string;
  description: string;
  inputSchema: Record<string, unknown>;
}

export interface MCPToolResult {
  content: Array<{
    type: 'text' | 'image' | 'resource';
    text?: string;
    data?: string;
    mimeType?: string;
  }>;
  isError?: boolean;
}

export interface MCPRequest {
  jsonrpc: '2.0';
  id: string;
  method: string;
  params?: Record<string, unknown>;
}

export interface MCPResponse {
  jsonrpc: '2.0';
  id: string;
  result?: unknown;
  error?: {
    code: number;
    message: string;
    data?: unknown;
  };
}

export class TESMCPClient {
  private baseUrl: string;
  private accessToken: string;
  private clientId: string;
  private schemaCache: string | null = null;

  constructor(baseUrl: string, accessToken: string, clientId: string) {
    this.baseUrl = baseUrl;
    this.accessToken = accessToken;
    this.clientId = clientId;
  }

  /**
   * Send an MCP request to the TES server
   */
  private async sendRequest(method: string, params?: Record<string, unknown>): Promise<MCPResponse> {
    const request: MCPRequest = {
      jsonrpc: '2.0',
      id: generateUUID(),
      method,
      params,
    };

    const response = await fetch(`${this.baseUrl}/api/mcp`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${this.accessToken}`,
        'x-client-id': this.clientId,
        'x-tes-environment': getEnvironment(),
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      throw new Error(`MCP request failed: ${response.status} ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * List available tools
   */
  async listTools(): Promise<MCPTool[]> {
    const response = await this.sendRequest('tools/list');
    if (response.error) {
      throw new Error(response.error.message);
    }
    return (response.result as { tools: MCPTool[] }).tools;
  }

  /**
   * Call a tool with arguments
   */
  async callTool(name: string, args: Record<string, unknown> = {}): Promise<MCPToolResult> {
    const response = await this.sendRequest('tools/call', {
      name,
      arguments: args,
    });

    if (response.error) {
      throw new Error(response.error.message);
    }

    return response.result as MCPToolResult;
  }

  /**
   * Introspect the GraphQL schema
   */
  async introspectSchema(): Promise<string> {
    if (this.schemaCache) {
      return this.schemaCache;
    }

    const result = await this.callTool('introspect_schema');
    const text = result.content.find((c) => c.type === 'text')?.text || '';
    this.schemaCache = text;
    return text;
  }

  // =========================================================================
  // THINGS - Physical items tracked through lifecycle
  // =========================================================================

  /**
   * Query things (physical items) with optional filters
   */
  async queryThings(
    filter?: Record<string, unknown>,
    options?: { sort?: string; limit?: number; offset?: number }
  ) {
    return this.callTool('query_things', { filter, ...options });
  }

  /**
   * Get a single thing by ID
   */
  async getThing(id: string) {
    return this.callTool('get_thing', { id });
  }

  /**
   * Create a new thing
   */
  async createThing(input: Record<string, unknown>) {
    return this.callTool('create_thing', { input });
  }

  /**
   * Update a thing
   */
  async updateThing(id: string, input: Record<string, unknown>) {
    return this.callTool('update_thing', { id, input });
  }

  /**
   * Add a status entry to a thing's lifecycle
   */
  async addThingStatus(id: string, input: { parent_status: string; status: string; [key: string]: unknown }) {
    return this.callTool('add_thing_status', { id, input });
  }

  // =========================================================================
  // HOLDERS - Entities that hold/own things
  // =========================================================================

  /**
   * Query holders (customers, warehouses, stores, etc.)
   */
  async queryHolders(
    filter?: Record<string, unknown>,
    options?: { sort?: string; limit?: number; offset?: number }
  ) {
    return this.callTool('query_holders', { filter, ...options });
  }

  /**
   * Get a single holder by ID
   */
  async getHolder(id: string) {
    return this.callTool('get_holder', { id });
  }

  /**
   * Create a new holder
   */
  async createHolder(input: { type: string; name?: string; email?: string; [key: string]: unknown }) {
    return this.callTool('create_holder', { input });
  }

  // =========================================================================
  // LOCATIONS - Physical places where things can be
  // =========================================================================

  /**
   * Query locations (warehouses, stores, processing centers, etc.)
   */
  async queryLocations(
    filter?: Record<string, unknown>,
    options?: { sort?: string; limit?: number; offset?: number }
  ) {
    return this.callTool('query_locations', { filter, ...options });
  }

  /**
   * Get a single location by ID
   */
  async getLocation(id: string) {
    return this.callTool('get_location', { id });
  }

  /**
   * Create a new location
   */
  async createLocation(input: { type?: string; name?: string; [key: string]: unknown }) {
    return this.callTool('create_location', { input });
  }

  // =========================================================================
  // LEGACY - Customers and Takebacks (D1 database)
  // =========================================================================

  /**
   * Query customers (legacy D1 database)
   * @deprecated Use queryHolders for the new ArangoDB-based system
   */
  async queryCustomers(filter?: { email?: string; name?: string }, pagination?: { limit?: number; offset?: number }) {
    return this.callTool('query_customers', { filter, pagination });
  }

  /**
   * Query takebacks (legacy D1 database)
   * @deprecated Use queryThings for the new event-sourced system
   */
  async queryTakebacks(
    filter?: { status?: string; customer_id?: string },
    pagination?: { limit?: number; offset?: number }
  ) {
    return this.callTool('query_takebacks', { filter, pagination });
  }

  // =========================================================================
  // GENERIC
  // =========================================================================

  /**
   * Execute a GraphQL query
   */
  async executeGraphQL(query: string, variables?: Record<string, unknown>) {
    return this.callTool('execute_graphql', { query, variables });
  }

  /**
   * Get a summary of available data for LLM context
   */
  async getSchemaContext(): Promise<string> {
    // Cache the schema introspection
    await this.introspectSchema();

    // Return a summarized version for LLM context
    return `
TES GraphQL API Schema Summary:

PRIMARY ENTITIES (ArangoDB graph database):

THINGS - Physical items tracked through lifecycle stages:
- Query: things(filter, sort, limit, offset)
- Fields: id, sku, name, current_stage, current_status, quantity, unit, condition
- Related: holder (current owner), location (current place), status_history
- Lifecycle stages: manufactured → in_stock → sold → in_use → returned → processing → processed → recycled

HOLDERS - Entities that hold/own things:
- Query: holders(filter, sort, limit, offset)
- Types: customer, warehouse, store, carrier, processor, manufacturer
- Fields: id, type, name, last_name, email, phone, address

LOCATIONS - Physical places where things can be:
- Query: locations(filter, sort, limit, offset)
- Types: warehouse, store, processing_center, distribution_center, customer_address
- Fields: id, type, name, address, coordinates, timezone

LEGACY ENTITIES (D1 SQLite - deprecated):
- takebacks(filter, pagination): Legacy takeback records
- customers(filter, pagination): Legacy customer records

COMMON FILTERS:
- Things: {current_stage: 'returned'}, {sku: 'PROD-123'}, {holder_id: 'uuid'}
- Holders: {type: 'customer'}, {email: 'user@example.com'}
- Locations: {type: 'warehouse'}, {'address.city': 'Berlin'}

Use execute_graphql tool for complex queries joining multiple entities.
    `.trim();
  }
}

/**
 * MCP tool result with optional structured content
 */
export interface MCPToolResultWithData extends MCPToolResult {
  structuredContent?: unknown;
}

/**
 * Extract data from MCP tool result
 * Prefers structuredContent (raw JSON) over content[].text (human-readable summary)
 */
export function extractMCPData(result: MCPToolResult | MCPToolResultWithData): unknown {
  // First check for structuredContent (raw JSON data)
  if ('structuredContent' in result && result.structuredContent != null) {
    return result.structuredContent;
  }

  // Fallback to text content
  const textContent = result.content.find((c) => c.type === 'text');
  if (!textContent?.text) return null;

  try {
    return JSON.parse(textContent.text);
  } catch {
    return textContent.text;
  }
}
